import * as _ from 'lodash';
import * as qs from 'qs';
import { Fragment, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { MenuChangeEvent } from '@szhsin/react-menu';
import type { VoidFunctionComponent } from 'react';

import { isProjects } from '../../../../shared/projectsV2/helpers';
import { Action, ResourceType } from '../../../../typings/permissions';
import { useAddToast } from '../../../contexts/ToastContext';
import { window } from '../../../globals';
import { useHasPermissionForTeamProjects } from '../../../helpers/permissions';
import { useOrganizationLabels } from '../../../hooks/api/organization/useOrganizationLabels';
import useTeams from '../../../hooks/api/teams/useTeams';
import * as ProjectsV2Store from '../../../redux/projectsV2';
import TeamsModule from '../../../redux/teams';
import useHasPermission from '../../../shared/permissions/useHasPermission';
import { PolicyType } from '../../../types';
import utils from '../../../utils';
import Button from '../../generic/Button/Button';
import { icons } from '../../generic/Icon/icons';
import { Icon, IconText } from '../../generic/Icon/index';
import RouterLink from '../../generic/Link/RouterLink';
import Loader from '../../generic/Loader';
import {
  Menu,
  MenuDivider,
  MenuGroup,
  MenuHeader,
  MenuItem,
  SubMenu,
} from '../../generic/Menu';
import useGroupedPolicies from '../../shared/policies/hooks/useGroupedPolicies';
import useUserPermittedPolicyTypes from '../../shared/policies/hooks/useUserPermittedPolicyTypes';

import useFetchProjectsAction from './hooks/useFetchProjectsAction';
import useShowReleaseGroupLimitModal from './hooks/useShowReleaseGroupLimitModal';
import { DeleteProjectsModal, DeleteReleaseGroupsModal } from './modals/DeleteModal';
import { RemoveTeamModal } from './modals/RemoveTeamModal';

const ACTION_REMOVE = -1;
const ACTION_MANAGE = -2;

type CreateReleaseGroupItemProps = {
  isAtOrOverLimit: boolean;
  createReleaseGroupUrl: string;
};

const CreateReleaseGroupItem: VoidFunctionComponent<CreateReleaseGroupItemProps> = ({
  isAtOrOverLimit,
  createReleaseGroupUrl,
}) => {
  const showReleaseGroupLimitModal = useShowReleaseGroupLimitModal();

  if (isAtOrOverLimit) {
    return (
      <MenuItem
        onClick={showReleaseGroupLimitModal}
      >
        Create release group...
      </MenuItem>
    );
  }

  return (
    <MenuItem>
      <RouterLink className="szh-menu__item--anchor" to={createReleaseGroupUrl}>
        Create release group...
      </RouterLink>
    </MenuItem>
  );
};

type Props = {
  shouldRestrictCreateReleaseGroup: boolean;
};

const ProjectActionsMenu: VoidFunctionComponent<Props> = ({ shouldRestrictCreateReleaseGroup }) => {
  const addToast = useAddToast();

  // Local State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRemoveTeamModalOpen, setIsRemoveTeamModalOpen] = useState(false);
  //  These two "switches" are used to defer the fetching of teams and policies.
  //  They are only ever set once when the relevant SubMenu is opened.
  const [shouldFetchTeams, setShouldFetchTeams] = useState(false);
  const [shouldFetchPoliciesSwitch, setShouldFetchPoliciesSwitch] = useState(false);

  //  Permissions
  const hasPermission = useHasPermission();
  const canAddProjectToAtLeastOneTeam = useMemo(() => {
    const usersTeams = window.user?.teams ?? [];
    const canAddProjectToAnyTeam = hasPermission({ resourceType: ResourceType.TeamProjectAny, action: Action.Add });
    const canAddProjectToSomeTeam = usersTeams.some((team) =>
      hasPermission({ action: Action.Add, resourceType: ResourceType.TeamProject, teamId: team.id }));
    return canAddProjectToAnyTeam || canAddProjectToSomeTeam;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- It's best practice to include all the dependencies, even if window.user is stable
  }, [hasPermission, window.user?.teams]);
  const canEditAnyTeam = useMemo(() => {
    const usersTeams = window.user?.teams ?? [];
    const canEditAny = hasPermission({ resourceType: ResourceType.TeamAny, action: Action.Edit });
    const canEditSome = usersTeams.some((team) =>
      hasPermission({ action: Action.Edit, resourceType: ResourceType.Team, teamId: team.id }));
    return canEditAny || canEditSome;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- It's best practice to include all the dependencies, even if window.user is stable
  }, [hasPermission, window.user?.teams]);
  const canEditAnyReleaseGroups = hasPermission({ action: Action.Edit, resourceType: ResourceType.ReleaseGroupAny });
  const canCreateReleaseGroups = hasPermission({ action: Action.Create, resourceType: ResourceType.ReleaseGroup });

  //  Policies
  const shouldOmitRules = true; // This ensures we don't join Rules into the Policy list, which a heavy join.
  const userPermittedPolicyTypes = useUserPermittedPolicyTypes();
  const shouldFetchPolicies = shouldFetchPoliciesSwitch && !_.isEmpty(userPermittedPolicyTypes);
  const {
    groupedPolicies,
    isLoading: isFetchingPolicies,
  } = useGroupedPolicies(shouldOmitRules, shouldFetchPolicies);
  const hasNoPolicies = groupedPolicies.size === 0;

  // Redux State
  const dispatch = useDispatch();
  const category = useSelector(ProjectsV2Store.selectors.getCategory);
  const areProjectSelected = useSelector(ProjectsV2Store.selectors.getAreSomeProjectsSelected);
  const selectedProjects = useSelector(ProjectsV2Store.selectors.getSelectedProjects);
  const selectedProjectsCount = useSelector(ProjectsV2Store.selectors.getSelectedProjectCount);
  const selectedProjectLocators = useSelector(ProjectsV2Store.selectors.getSelectedProjectLocators);
  const filters = useSelector(ProjectsV2Store.selectors.getProjectsFilterQuery);
  const teamSelection = useSelector(TeamsModule.selectors.getTeamSelection);

  //  Org Labels (React Query)
  const { data: labelsData } = useOrganizationLabels();
  const labels = useMemo(
    () => _.sortBy(labelsData?.labels ?? [], (label) => label.label.toLowerCase()),
    [labelsData],
  );

  //  React Query (teams)
  const { data: teams, isLoading: isFetchingTeams } = useTeams(shouldFetchTeams);
  const hasPermissionForTeamProjects = useHasPermissionForTeamProjects();
  const availableTeams = useMemo(() =>
    teams?.filter((team) => hasPermissionForTeamProjects(Action.Add, team.id)) ?? [],
  [teams, hasPermissionForTeamProjects]);
  // only allow bulk project team removal when a single team is selected
  const canRemoveFromTeam =
    teamSelection !== 'null' &&
    teamSelection.length === 1 &&
    hasPermissionForTeamProjects(Action.Remove, teamSelection[0] as number);
  const activeTeamName = useMemo(
    () => teams?.find((team) => team.id === Number(teamSelection[0]))?.name ?? 'the team',
    [teams, teamSelection],
  );

  const createReleaseGroupUrl = `/projects/group/create?${qs.stringify({
    selected: selectedProjectLocators,
    ...filters,
  })}`;

  const handleShowDeleteModal = () =>
    setIsDeleteModalOpen(true);

  const handleShowRemoveTeamModal = () =>
    setIsRemoveTeamModalOpen(true);

  const addTeamToProject = useFetchProjectsAction(TeamsModule.actions.addTeamProjects);

  const onAddToTeam = async (teamId: number) => {
    addTeamToProject(teamId, selectedProjectLocators, filters);
    dispatch(ProjectsV2Store.actions.clearSelection());
    addToast({ type: 'success', content: 'Successfully added project(s) to team.' });
  };

  const onAddLabelToProjects = useFetchProjectsAction(ProjectsV2Store.actions.bulkApplyLabel);

  const addLabelToProjects = async (labelId: number) => {
    onAddLabelToProjects(labelId, addToast);
    dispatch(ProjectsV2Store.actions.clearSelection());
  };

  const onAssignPolicyToProjects = useFetchProjectsAction(ProjectsV2Store.actions.bulkApplyPolicy);

  const assignPolicyToProjects = async (policyId: number) => {
    onAssignPolicyToProjects(policyId, addToast);
    dispatch(ProjectsV2Store.actions.clearSelection());
  };

  return (
    <>
      {isProjects(category) ? (
        <DeleteProjectsModal
          show={isDeleteModalOpen}
          selectedProjects={selectedProjects}
          selectedProjectsCount={selectedProjectsCount}
          onHide={() => setIsDeleteModalOpen(false)}
          onDelete={() => setIsDeleteModalOpen(false)}
        />
      ) : (
        <DeleteReleaseGroupsModal
          show={isDeleteModalOpen}
          selectedReleaseGroups={selectedProjects}
          selectedReleaseGroupsCount={selectedProjectsCount}
          onHide={() => setIsDeleteModalOpen(false)}
          onDelete={() => setIsDeleteModalOpen(false)}
        />
      )}
      <RemoveTeamModal
        show={isRemoveTeamModalOpen}
        selectedProjects={selectedProjects}
        selectedProjectsCount={selectedProjectsCount}
        onHide={() => setIsRemoveTeamModalOpen(false)}
        onDelete={() => setIsRemoveTeamModalOpen(false)}
        teamName={activeTeamName}
      />
      <Menu
        menuButton={
          <Button type="secondary" size="large" isDisabled={!areProjectSelected}>
            <div className="project-actions-menu">
              <span>Actions</span>
              <Icon icon={icons.caretDown} />
            </div>
          </Button>
        }
        boundingBoxPadding="100% 0"
        gap={4}
      >
        {isProjects(category) && (
          <SubMenu
            label="Add to team"
            disabled={!canAddProjectToAtLeastOneTeam}
            menuClassName="team-nested-menu"
            setDownOverflow
            overflow="auto"
            position="auto"
            gap={8}
            onMenuChange={(event: MenuChangeEvent) => {
              //  This is a single-use switch. Once the menu is opened, this remains enabled.
              if (!shouldFetchTeams && event.open) {
                setShouldFetchTeams(true);
              }
            }}
          >
            <MenuGroup takeOverflow>
              {isFetchingTeams ? (
                <MenuItem>
                  <Loader />
                </MenuItem>
              ) : (
                availableTeams?.map((team) => (
                  <MenuItem key={team.id} onClick={() => onAddToTeam(team.id)}>
                    <span style={{ color: utils.hashColor(team.name) }}>
                      <Icon icon={icons.square} />
                      {team.name}
                    </span>
                  </MenuItem>
                )))}
              {!isFetchingTeams && availableTeams.length === 0 && (
                <MenuItem>
                  No teams found
                </MenuItem>
              )}
              {!isFetchingTeams && canEditAnyTeam && (
                <MenuItem key={ACTION_MANAGE}>
                  <RouterLink className="szh-menu__item--anchor" to="/account/settings/organization/teams">
                    <span>
                      <Icon icon={icons.cog} />
                      Manage Teams
                    </span>
                  </RouterLink>
                </MenuItem>
              )}
            </MenuGroup>
          </SubMenu>
        )}
        {canRemoveFromTeam && (
          <MenuItem key={ACTION_REMOVE} onClick={handleShowRemoveTeamModal}>
            Remove from team
          </MenuItem>
        )}
        {isProjects(category) && (
          <SubMenu
            label="Assign label"
            menuClassName="label-nested-menu"
            setDownOverflow
            overflow="auto"
            position="auto"
            align="center"
            gap={8}
          >
            <MenuGroup takeOverflow>
              {labels.map((label) => (
                <MenuItem
                  key={label.id}
                  onClick={() => {
                    addLabelToProjects(label.id);
                  }}
                >
                  {label.label}
                </MenuItem>
              ))}
            </MenuGroup>
          </SubMenu>
        )}
        <SubMenu
          label="Assign policy"
          setDownOverflow
          overflow="auto"
          position="auto"
          align="center"
          gap={8}
          onMenuChange={(event: MenuChangeEvent) => {
            //  This is a single-use switch. Once the menu is opened, this remains enabled.
            if (event.open && !shouldFetchPoliciesSwitch) {
              setShouldFetchPoliciesSwitch(true);
            }
          }}
        >
          <MenuGroup>
            {isFetchingPolicies && (
              <MenuItem>
                <Loader />
              </MenuItem>
            )}
            {!isFetchingPolicies && hasNoPolicies && (
              <MenuItem>
                No policies found
              </MenuItem>
            )}
            {[ //  We do it this way to ensure the policies are sorted properly.
              PolicyType.LICENSING,
              PolicyType.SECURITY,
              PolicyType.QUALITY,
              PolicyType.SBOM,
            ].map((type: PolicyType, typeIdx: number) => {
              const policies = groupedPolicies.get(type);
              if (!policies) {
                return null;
              }

              return (
                <Fragment key={type}>
                  <MenuHeader>{type}</MenuHeader>
                  {policies.map((policy) => (
                    <MenuItem
                      key={policy.id}
                      onClick={() => assignPolicyToProjects(policy.id)}
                    >
                      {policy.title}
                    </MenuItem>
                  ))}
                  {typeIdx !== groupedPolicies.size - 1 && <MenuDivider />}
                </Fragment>
              );
            })}
          </MenuGroup>
          <MenuDivider />
          <MenuItem>
            <RouterLink className="szh-menu__item--anchor" to="/policies">
              <IconText icon={icons.gear} text="Manage Policies" />
            </RouterLink>
          </MenuItem>
        </SubMenu>
        {(canEditAnyReleaseGroups || canCreateReleaseGroups) && isProjects(category) && (
          <CreateReleaseGroupItem
            isAtOrOverLimit={shouldRestrictCreateReleaseGroup}
            createReleaseGroupUrl={createReleaseGroupUrl}
          />
        )}
        <MenuItem className="fossa-menu__item-danger" onClick={handleShowDeleteModal}>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export default ProjectActionsMenu;
